import { useMemo, useRef, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { useEffectiveClient } from '@/hooks/useAuth'
import { usePlan } from '@/hooks/useConfig'
import { useProfile } from '@/hooks/useProfile'
import {
  useAffiliateState, useApply, useIssueCode, useSetCodeActive, simulateBook, type Affiliate as Aff, type ReferralCode,
} from '@/hooks/useAffiliate'
import '@/styles/affiliate.css'

// Placeholders until Brian sets them: every dollar figure below derives from
// these, and every cell that shows one is shaded to say so.
const RATE = 0.25
const PRICES = { sprout: 79, grove: 149, harvest: 299 } as const
const ADDON = { name: 'Newsletter Builder', price: 59, commission: 15 }
type Tier = keyof typeof PRICES
const TIERS: Tier[] = ['sprout', 'grove', 'harvest']
const cap = (t: string) => t[0].toUpperCase() + t.slice(1)

const CHANNELS: [string, string][] = [
  ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['email', 'Email list'],
  ['youtube', 'YouTube'], ['inperson', 'In person'], ['brokerage', 'Brokerage or team'],
]
const SIZES: [string, string][] = [['500', 'Under 500'], ['1k', '500 to 2,000'], ['10k', '2,000 to 10,000'], ['10k+', 'Over 10,000']]
const AGREEMENTS: [string, string][] = [
  ['disclose', 'I\'ll disclose that I earn a commission whenever I recommend GoGrov.'],
  ['own', 'I understand I can only earn on add-ons I subscribe to myself, and that Harvest already includes both.'],
  ['terms', 'I\'ve read the program terms: a referral pays after one completed billing cycle, commission ends if they cancel or pause, I fund any discount I offer out of my own payout, and commission is reversed if a payment is refunded within 30 days.'],
  ['account', 'I understand commission is only paid while my own subscription is active. If I cancel, earning ends permanently after my last month, and putting my account on hold ($12 a month, once per 6 months) stops earning entirely while my code book keeps shrinking, with earning restarting after my first paid month back.'],
]

export function Affiliate() {
  const { data, isLoading } = useAffiliateState()
  const [applying, setApplying] = useState(false)
  if (isLoading || !data) return <div className="gg-legacy"><span className="gg-spinner" /></div>
  if (data.affiliate) return <Dashboard aff={data.affiliate} codes={data.codes} referrals={data.referrals} events={data.events} />
  if (data.application?.status === 'pending') return <Pending />
  if (applying) return <Apply onBack={() => setApplying(false)} declined={data.application?.status === 'declined' ? data.application.reviewed_at : null} />
  return <Pitch onApply={() => setApplying(true)} declinedAt={data.application?.status === 'declined' ? data.application.reviewed_at : null} />
}

function Money({ v, placeholder = true }: { v: number; placeholder?: boolean }) {
  const s = `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  return <span className={placeholder ? 'aff-ph' : ''}>{s}</span>
}

function Pitch({ onApply, declinedAt }: { onApply: () => void; declinedAt: string | null }) {
  const qa = useRef<HTMLDivElement>(null)
  const retry = declinedAt ? new Date(new Date(declinedAt).getTime() + 90 * 86400000) : null
  const canApply = !retry || retry <= new Date()
  return (
    <div className="gg-legacy aff">
      <section className="aff-hero">
        <img className="aff-hero__photo" src="/affiliate/hero-mobile.webp" alt="" />
        <div className="aff-hero__copy">
          <span className="aff-hero__eyebrow"><img src="/brand/sprout-mark.png" alt="" width={26} height={24} /> Grow with us.</span>
          <h1>Earn on every month your referrals stay.</h1>
          <p>Refer someone to GoGrov and you earn a share of what they pay, not once, but every month they keep paying.</p>
        </div>
      </section>
      <p className="aff-lede">
        Commission is recurring. It is deducted from your own subscription first, and the remainder is paid to you on your
        billing date. Applications are read by a person, so there is a wait before you are in.{' '}
        <button className="aff-link" onClick={() => qa.current?.scrollIntoView({ behavior: 'smooth' })}>Questions and answers</button>
      </p>

      <div className="aff-principles">
        {[['Recurring', 'A referral who stays two years pays you for two years. There is no one-time bounty.'],
          ['One completed cycle', 'A referral pays you once they have completed a full billing cycle. Not 90 days: one month.'],
          ['Your discount to set', 'Offer up to 20% off a first month. It comes out of your first payout, never GoGrov\'s price.']].map(([t, b]) => (
          <div key={t} className="gg-panel"><h3>{t}</h3><p>{b}</p></div>
        ))}
      </div>

      <Calculator />

      <section className="aff-section">
        <h2>What we&rsquo;ll tell you before you apply.</h2>
        <div className="aff-disclosures">
          {[['Approval is by hand.', 'We read every application. It isn\'t instant and we can say no.'],
            ['Commission pays your own bill first.', 'What you earn is deducted from your subscription. Only the surplus reaches you.'],
            ['Your code book doubles as you convert.', 'Work through your codes and the book doubles, up to 32. Sharing a code costs nothing; only a confirmed signup spends a slot.'],
            ['Commission stops when they leave.', 'If a referral cancels or pauses, that commission ends with their last completed cycle. A paused account resumes paying you once it completes a cycle again.'],
            ['You have to keep your own account.', 'Commission is only paid while your subscription is active. If you cancel, you\'re paid for your last month and every future split ends permanently.'],
            ['You can put things on hold.', 'Step away for a season and keep your account for $12 a month, once in any 6-month window. Earning stops while you\'re away, and your code book carries on shrinking.']].map(([t, b]) => (
            <div key={t}><strong>{t}</strong><p>{b}</p></div>
          ))}
        </div>
      </section>

      <BookSimulator />

      <section className="aff-section">
        <h2>To take part</h2>
        <ul className="aff-reqs">
          <li>You hold an active GoGrov subscription, any tier.</li>
          <li>You subscribe to an add-on yourself before you can earn on it, and for as long as you earn on it.</li>
          <li>You disclose the relationship when you recommend GoGrov.</li>
          <li>Your referral uses your code at signup. A line without a code doesn&rsquo;t pay.</li>
        </ul>
        <button className="gg-btn gg-btn--amber aff-cta" onClick={onApply} disabled={!canApply}>Apply to the affiliate program</button>
        <p className="gg-help">{canApply ? 'Takes about a minute. A person reviews it, and we\'ll email you either way.' : `You can apply again on ${retry!.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}.`}</p>
      </section>

      <section className="aff-section" ref={qa}>
        <h2>Questions and answers</h2>
        <div className="aff-qa">{QA.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}</div>
      </section>
      <p className="aff-foot">Figures on shaded cells are placeholders and are not an offer. Commission rate, add-on pricing and allotment limits are not final. Program terms are pending legal review.</p>
    </div>
  )
}

function Calculator() {
  const [mine, setMine] = useState<Tier>('grove')
  const [theirs, setTheirs] = useState<Tier>('grove')
  const [count, setCount] = useState(5)
  const [attach, setAttach] = useState(2)
  const [bought, setBought] = useState(false)
  // Rounded once at source; everything else derives from it, so the sums a
  // reader does in their head match the totals.
  const per = Math.round(PRICES[theirs] * RATE)
  const sellable = mine === 'harvest' || (mine === 'grove' && bought)
  const addonCount = theirs === 'harvest' || !sellable ? 0 : Math.min(attach, count)
  const subs = per * count
  const addons = ADDON.commission * addonCount
  const total = subs + addons
  const bill = PRICES[mine] + (mine === 'grove' && bought ? ADDON.price : 0)
  const net = total - bill
  const breakEven = Math.ceil(bill / Math.max(per, 1))
  const verdict = count === 0 ? 'Nothing yet. Your first referrals cover your own bill.'
    : net < 0 ? `Covering ${Math.round((total / bill) * 100)}% of your own bill. About ${breakEven} referrals pays for it outright.`
    : net === 0 ? 'Your subscription is paid for exactly.' : `Your subscription is covered, and ${'$' + net} reaches you.`

  return (
    <section className="aff-section gg-panel">
      <span className="eyebrow eyebrow--accent">Try it</span>
      <h2>What you&rsquo;d make</h2>
      <p className="gg-help">Commission covers your own bill before anything reaches you, so the first few referrals pay for your subscription rather than your pocket.</p>
      <div className="aff-calc">
        <div className="aff-calc__controls">
          <div className="gg-field"><span>Your package</span><div className="gg-seg">{TIERS.map((t) => <button key={t} aria-pressed={mine === t} onClick={() => setMine(t)}>{cap(t)}</button>)}</div></div>
          <div className="gg-field"><span>What they sign up for</span><div className="gg-seg">{TIERS.map((t) => <button key={t} aria-pressed={theirs === t} onClick={() => setTheirs(t)}>{cap(t)}</button>)}</div></div>
          <label className="gg-field"><span>Referrals paying you · {count}</span><input className="aff-range" type="range" min={0} max={100} value={count} onChange={(e) => setCount(Number(e.target.value))} /></label>
          {sellable && theirs !== 'harvest' && (
            <label className="gg-field"><span>And how many add the {ADDON.name} · {Math.min(attach, count)}</span><input className="aff-range" type="range" min={0} max={count} value={Math.min(attach, count)} onChange={(e) => setAttach(Number(e.target.value))} /></label>
          )}
        </div>
        <div className="aff-ledger">
          <div><span>{count} × <Money v={per} /> subscription commission</span><Money v={subs} /></div>
          <div><span>{addonCount} × <Money v={ADDON.commission} /> add-on commission</span><Money v={addons} /></div>
          <div className="is-total"><span>Total earned</span><Money v={total} /></div>
          <div><span>Your own subscription</span><Money v={-bill} /></div>
          <div className="is-total"><span>Reaches you</span><Money v={Math.max(0, net)} /></div>
          <p className="aff-verdict">{verdict}</p>
        </div>
      </div>
      <div className="aff-unlock">
        <strong>What you can sell on your package</strong>
        <div className="aff-unlock__row">
          <span>{ADDON.name}</span>
          {mine === 'sprout' && <em>Not available on Sprout. You can&rsquo;t run it, so you can&rsquo;t sell it.</em>}
          {mine === 'harvest' && <em>Included and unlocked.</em>}
          {mine === 'grove' && (bought
            ? <em>Unlocked. <button className="aff-link" onClick={() => setBought(false)}>Remove</button></em>
            : <em>Locked until you subscribe. <button className="gg-btn gg-btn--sm" onClick={() => setBought(true)}>Add for <Money v={ADDON.price} /></button></em>)}
        </div>
        {mine === 'grove' && <p className="gg-help">The trade is real: <Money v={ADDON.price} /> onto your bill for <Money v={ADDON.commission} /> per attached referral, which only pays back above four of them.</p>}
      </div>
      <p className="aff-foot">Placeholder: the {Math.round(RATE * 100)}% commission rate and add-on pricing are not final.</p>
    </section>
  )
}

function BookSimulator() {
  const [stuck, setStuck] = useState(6)
  const [churn, setChurn] = useState(2)
  const [quiet, setQuiet] = useState(0)
  const sim = useMemo(() => simulateBook(stuck, churn, quiet), [stuck, churn, quiet])
  return (
    <section className="aff-section gg-panel">
      <span className="eyebrow eyebrow--accent">Try it</span>
      <h2>How the code book moves</h2>
      <p className="gg-help">You start with 5. Move the sliders to see what your book does over a year.</p>
      <Ladder book={sim.book} start={sim.start} />
      <p className="aff-book"><b>{sim.book}</b> codes · {sim.verdict}</p>
      <div className="aff-calc__controls">
        <label className="gg-field"><span>Referrals who stuck · {stuck}</span><input className="aff-range" type="range" min={0} max={24} value={stuck} onChange={(e) => setStuck(Number(e.target.value))} /></label>
        <label className="gg-field"><span>Cancelled inside 90 days · {churn}</span><input className="aff-range" type="range" min={0} max={24} value={churn} onChange={(e) => setChurn(Number(e.target.value))} /></label>
        <label className="gg-field"><span>Then months with nobody signing up · {quiet}</span><input className="aff-range" type="range" min={0} max={12} value={quiet} onChange={(e) => setQuiet(Number(e.target.value))} /></label>
        <p className="gg-help">The first 3 are free. After that the book loses 1 a month. Months on hold count too.</p>
      </div>
      <ol className="aff-log">{sim.log.slice(-10).map((l, i) => <li key={i}><b>{l.delta}</b><span>{l.reason}</span><em>{l.to}</em></li>)}</ol>
      <p className="gg-help">Sharing a code never costs a slot. Only a confirmed signup does, and once you&rsquo;re at 32 even that hands the slot straight back. The book never falls below 1, so there&rsquo;s always a way back.</p>
      <p className="aff-foot">Placeholder: the starting book size of 5 is not final.</p>
    </section>
  )
}

function Ladder({ book, start, available, frozen }: { book: number; start?: number; available?: number; frozen?: number }) {
  return (
    <div className="aff-ladder" role="img" aria-label={`${book} of 32 codes`}>
      {Array.from({ length: 32 }, (_, i) => {
        let cls = ''
        if (available != null) cls = i < available ? 'is-avail' : i < available + (frozen ?? 0) ? 'is-frozen' : i < book ? 'is-spent' : ''
        else cls = i < book ? (start && i < start ? 'is-start' : 'is-avail') : ''
        return <i key={i} className={cls} />
      })}
    </div>
  )
}

function Apply({ onBack, declined }: { onBack: () => void; declined: string | null }) {
  const user = useAuthStore((s) => s.user)
  const client = useEffectiveClient()
  const { data: plan } = usePlan()
  const { data: profile } = useProfile()
  const apply = useApply()
  const [channels, setChannels] = useState<string[]>([])
  const [size, setSize] = useState('1k')
  const [url, setUrl] = useState('')
  const [audience, setAudience] = useState('')
  const [approach, setApproach] = useState('')
  const [code, setCode] = useState('')
  const [agreed, setAgreed] = useState<Record<string, string>>({})
  const [err, setErr] = useState<string | null>(null)
  const ready = channels.length > 0 && Object.keys(agreed).length === 4 && code.trim().length >= 3
  const why = channels.length === 0 ? 'Pick at least one place you reach people.'
    : code.trim().length < 3 ? 'Choose a referral code of at least 3 characters.'
    : Object.keys(agreed).length < 4 ? 'Agree to all four above to send your application.' : ''

  return (
    <div className="gg-legacy aff aff--form">
      <button className="gg-btn gg-btn--sm gg-btn--quiet" onClick={onBack}>Back</button>
      <span className="eyebrow eyebrow--accent">Affiliate program · Apply</span>
      <h1 className="aff-title">Tell us how you&rsquo;d recommend GoGrov.</h1>
      <p className="gg-help">Six questions. A person reads every one. We&rsquo;re looking for people who already talk to a local audience, not for volume. We&rsquo;ll email you either way, usually within 3 business days.</p>
      {declined && <p className="gg-note">Your last application was declined. You are welcome to apply again now.</p>}

      <div className="gg-panel aff-account">
        <span className="eyebrow">Your account <em>Prefilled</em></span>
        <strong>{profile?.full_name ?? user?.email} · {user?.email}</strong>
        <span>{plan?.name ?? 'Plan'} · {client?.city_state}</span>
      </div>

      <div className="gg-field"><span>Where do you reach people?</span>
        <div className="aff-chips">{CHANNELS.map(([k, l]) => <button key={k} aria-pressed={channels.includes(k)} onClick={() => setChannels((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k])}>{l}</button>)}</div>
      </div>
      <div className="gg-field"><span>Roughly how many people follow you?</span>
        <div className="aff-chips">{SIZES.map(([k, l]) => <button key={k} aria-pressed={size === k} onClick={() => setSize(k)}>{l}</button>)}</div>
        <p className="gg-help">Size isn&rsquo;t the deciding factor. We&rsquo;d rather see 400 local people than 40,000 strangers.</p>
      </div>
      <label className="gg-field"><span>Link to where you post</span><input className="gg-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="instagram.com/yourname" /></label>
      <label className="gg-field"><span>Who would you recommend GoGrov to?</span><textarea className="gg-textarea" rows={4} value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="Be specific. &quot;Agents in my brokerage&quot; tells us more than &quot;my network.&quot;" /></label>
      <label className="gg-field"><span>How do you plan to bring it up?</span><textarea className="gg-textarea" rows={3} value={approach} onChange={(e) => setApproach(e.target.value)} /></label>
      <label className="gg-field"><span>Pick your referral code</span>
        <div className="aff-code"><em>gogrov.com/r/</em><input value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} maxLength={24} aria-label="Referral code" /></div>
        <p className="gg-help">Letters, numbers and dashes. We&rsquo;ll confirm it&rsquo;s free when we approve you.</p>
      </label>

      <div className="gg-field"><span>What you&rsquo;re agreeing to</span>
        <div className="aff-agree">
          {AGREEMENTS.map(([k, l]) => (
            <label key={k}><input type="checkbox" checked={!!agreed[k]} onChange={(e) => setAgreed((a) => { const n = { ...a }; if (e.target.checked) n[k] = new Date().toISOString(); else delete n[k]; return n })} /><span>{l}</span></label>
          ))}
        </div>
        <p className="aff-foot">Placeholder: agreement wording is pending legal review.</p>
      </div>
      <button className="gg-btn gg-btn--amber aff-cta" disabled={!ready || apply.isPending} onClick={async () => {
        setErr(null)
        try { await apply.mutateAsync({ channels, size, url, audience, approach, code, agreements: agreed }) }
        catch (e) { setErr((e as Error).message) }
      }}>{apply.isPending ? 'Sending' : 'Send application'}</button>
      {!ready && <p className="gg-help">{why}</p>}
      {err && <p className="gg-error">{err}</p>}
      <p className="gg-help">Applying doesn&rsquo;t change your subscription. If we decline, you keep everything you have today and you&rsquo;re welcome to apply again in 90 days.</p>
    </div>
  )
}

function Pending() {
  return (
    <div className="gg-legacy aff aff--form">
      <span className="eyebrow eyebrow--accent">Affiliate program</span>
      <h1 className="aff-title">Your application is with us.</h1>
      <p className="gg-help">A person reads every application, usually within 3 business days. We&rsquo;ll email you either way. Nothing about your subscription changes in the meantime.</p>
    </div>
  )
}

function Dashboard({ aff, codes, referrals, events }: { aff: Aff; codes: ReferralCode[]; referrals: { id: string; state: string; joined_at: string; cycles_done: number; code_id: string | null }[]; events: { id: string; reason: string; delta: number; created_at: string }[] }) {
  const issue = useIssueCode()
  const setActive = useSetCodeActive()
  const [newCode, setNewCode] = useState('')
  const [discount, setDiscount] = useState(0)
  const [label, setLabel] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const paying = referrals.filter((r) => r.state === 'paying').length
  const notYet = referrals.filter((r) => r.state === 'first').length
  const nextDoubling = Math.min(32, aff.book_level * 2)
  const toDouble = aff.slots_available + aff.slots_frozen
  const link = `${window.location.origin}/r/${aff.code}`

  return (
    <div className="gg-legacy aff aff--dash">
      <span className="eyebrow eyebrow--accent">Affiliate program</span>
      <h1 className="aff-title">Your referrals</h1>
      <p className="gg-help">Your link: <b className="aff-mono">{link}</b> <button className="aff-link" onClick={() => void navigator.clipboard.writeText(link)}>Copy</button></p>

      <div className="aff-counters">
        <div className="gg-panel"><span className="eyebrow">Paying</span><b>{paying}</b></div>
        <div className="gg-panel"><span className="eyebrow">Not paying yet</span><b>{notYet}</b></div>
        <div className="gg-panel aff-counter--amber"><span className="eyebrow">Monthly commission</span><b className="aff-ph">Soon</b></div>
        <div className="gg-panel aff-counter--amber"><span className="eyebrow">Paid to you</span><b className="aff-ph">Soon</b></div>
      </div>
      <div className="gg-note">Commission, netting against your bill and payouts through PayPal or Wise switch on with billing. Until then your referrals and code book are tracked here, and nothing you earn is lost.</div>

      <section className="aff-section gg-panel">
        <h2>Code book</h2>
        <div className="aff-counters aff-counters--3">
          <div><span className="eyebrow">Available</span><b>{Math.max(aff.slots_available, 0)}</b></div>
          <div><span className="eyebrow">Frozen</span><b>{aff.slots_frozen}</b></div>
          <div><span className="eyebrow">Next doubling</span><b>{aff.book_level >= 32 ? 'At ceiling' : nextDoubling}</b></div>
        </div>
        <p className="gg-help">The ceiling is 32. Frozen is a hold, not a deduction: a slot waits there while a new referral finishes their first paid month.</p>
        <Ladder book={aff.book_level} available={aff.slots_available} frozen={aff.slots_frozen} />
        <div className="aff-progress"><i style={{ width: `${aff.book_level >= 32 ? 100 : Math.round((aff.slots_spent / Math.max(aff.book_level, 1)) * 100)}%` }} /></div>
        <p className="gg-help">{aff.book_level >= 32 ? 'At the ceiling, each confirmed month hands its slot straight back.' : `${toDouble} more confirmed ${toDouble === 1 ? 'signup' : 'signups'} and the book doubles to ${nextDoubling}.`}</p>
        <div className="aff-pips"><i className={aff.early_churns >= 1 ? 'is-on' : ''} /><i /><span>{aff.early_churns >= 1 ? '1 early cancellation logged. One more inside 90 days and the book drops by 1.' : 'No early cancellations logged.'}</span></div>

        <h3>Issue a code</h3>
        <p className="gg-help">For a group, a post or a person. Sharing it costs nothing; a slot is only spent when someone redeems it. You fund any discount, off your first payout on that referral.</p>
        <div className="aff-issue">
          <input className="gg-input aff-mono" value={newCode} onChange={(e) => setNewCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="code" maxLength={32} />
          <select className="gg-select" value={discount} onChange={(e) => setDiscount(Number(e.target.value))}>{[0, 5, 10, 15, 20].map((d) => <option key={d} value={d}>{d === 0 ? 'No discount' : `${d}% off month one`}</option>)}</select>
          <input className="gg-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" />
          <button className="gg-btn gg-btn--amber" disabled={newCode.length < 3 || issue.isPending} onClick={async () => { setErr(null); try { await issue.mutateAsync({ code: newCode, discount, label }); setNewCode(''); setLabel('') } catch (e) { setErr((e as Error).message) } }}>Issue</button>
        </div>
        {discount > 0 && <p className="gg-help">On Grove that is <Money v={Math.round(PRICES.grove * discount / 100)} /> off their first month, from your payout.</p>}
        {err && <p className="gg-error">{err}</p>}
        <div className="aff-codes">
          {codes.map((c) => (
            <div key={c.id} className="aff-coderow">
              <span className="aff-mono">{c.code}</span>
              <span>{c.discount_pct ? `${c.discount_pct}% off` : 'No discount'}{c.label ? ` · ${c.label}` : ''}</span>
              <em className={c.active ? 'is-live' : ''}>{c.active ? 'Live' : 'Off'}</em>
              {c.code !== aff.code && <button className="gg-btn gg-btn--sm gg-btn--quiet" onClick={() => setActive.mutate({ id: c.id, active: !c.active })}>{c.active ? 'Turn off' : 'Turn on'}</button>}
            </div>
          ))}
        </div>
        <h3>What changed</h3>
        <ol className="aff-log">{events.map((e) => <li key={e.id}><b>{e.delta > 0 ? `+${e.delta}` : e.delta < 0 ? `−${Math.abs(e.delta)}` : '±0'}</b><span>{e.reason}</span><em>{new Date(e.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</em></li>)}</ol>
      </section>

      <section className="aff-section gg-panel">
        <h2>Referrals</h2>
        {referrals.length === 0 ? <p className="gg-help">Nobody yet. When someone signs up with your code they appear here by ID, never by name.</p> : (
          <div className="aff-refs">
            {referrals.map((r) => (
              <div key={r.id} className="aff-refrow">
                <span className="aff-mono">{r.id}</span>
                <span>Joined {new Date(r.joined_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                <span>{r.cycles_done} {r.cycles_done === 1 ? 'cycle' : 'cycles'}</span>
                <em className={`aff-state aff-state--${r.state}`}>{r.state === 'first' ? 'First month' : r.state}</em>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="gg-note">You keep earning only while your own account is active. Cancelling ends every future split permanently.</div>
      <div className="gg-note">Say you earn a commission every time you recommend GoGrov.</div>
    </div>
  )
}

const QA: [string, string][] = [
  ['When does a referral start paying me?', 'After they complete one full billing cycle. Not 90 days: one month.'],
  ['What happens if they cancel?', 'Commission ends with their last completed cycle. Nothing is clawed back from what you\'ve already been paid.'],
  ['What if they pause instead?', 'A paused referral pays you nothing for as long as their subscription is paused. If they come back, it resumes once they complete a cycle again.'],
  ['When do I actually get the money?', 'On your own billing date, not theirs. Commission is applied to your subscription first, and the remainder is sent to your payout account.'],
  ['Can I earn on the add-ons?', 'Only the ones you subscribe to yourself. Harvest includes both, so a Harvest referral earns you the tier rate and nothing extra.'],
  ['What if I drop an add-on I\'ve been earning on?', 'Those payouts end with it. The program is built for people who know the product because they work in it every day, and that is the same reason you can only recommend what you\'re actually running.'],
  ['What happens when a referral upgrades or downgrades?', 'Your commission moves with them. A referral is tied to you for the life of their account, so an upgrade raises your monthly and a downgrade lowers it.'],
  ['What if I don\'t convert anyone for a while?', 'Nothing dramatic. You aren\'t removed from the program; your code book just shrinks toward its floor of 1. People convert in month five.'],
  ['Is there a minimum before you pay me?', 'Yes, $5. Anything below that stays on your balance and rolls forward to your next billing date. If you close your account with less than $5, we send it with your final payout.'],
  ['Do I have to handle tax paperwork with GoGrov?', 'No. Your W-9 and any tax reporting are handled by PayPal or Wise when you set up your payout account.'],
  ['How many codes do I get?', 'You hold a code book, and it grows with you. Work through it and it doubles, up to a ceiling of 32. Sharing a code publicly costs you nothing: a slot is only spent when someone redeems it and their first payment confirms.'],
  ['What happens once I\'m at 32?', 'The ladder stops, but you stop draining too. At the ceiling a confirmed month hands its slot straight back.'],
  ['What happens to my book if referrals cancel early?', 'A cancellation inside 90 days returns that slot to your book, and it\'s recorded as an early churn. Every second early churn takes one code off the book. Your book never drops below 1.'],
  ['Can I set the discount myself?', 'Yes, anything up to 20% off a first month. You fund it: the discount comes off your first payout on that referral, and GoGrov\'s price doesn\'t move.'],
  ['What happens if I cancel my own subscription?', 'You\'re paid your final month, and then earning ends permanently. Future splits on your referrals don\'t carry over, and they don\'t come back if you resubscribe later.'],
  ['What if I need to step away for a while?', 'You can put your account on hold: a quiet season, a move, a new baby. It\'s a break you choose, and nothing about your account is lost while you\'re gone.'],
  ['Do I keep earning while I\'m on hold?', 'No. Earning stops for the whole break and nothing is set aside for you. When you come back, earning restarts after your first paid month.'],
  ['What does a hold cost, and what stays?', '$12 a month, once in any 6-month window. That keeps your profile, your voice profile and all your settings exactly as you left them. Your codes stop working until you\'re back.'],
  ['Why does a hold cost anything at all?', 'Because it should be for stepping away rather than for saving money. Around 5 active referrals your commission covers your subscription outright, so if the account is paying for itself there is nothing to put on hold.'],
  ['What if a referral signs up and cancels straight away?', 'You keep that month\'s commission. The discount you funded comes off it, and the cancellation returns a slot to your book without doubling it.'],
  ['What if a referral gets refunded?', 'If the refund lands within 30 days of the payment, the commission on it is reversed off your next payout. After 30 days we absorb it. A refund doesn\'t touch your code book.'],
  ['Can I start my own Facebook support group?', 'Yes. Running a group for the people you\'ve referred is welcome. Say in the group that you earn a commission, and don\'t present it as official GoGrov support.'],
  ['Can I see or publish my referrals\' names?', 'No. You see a pseudonymous ID per referral, never a name or contact details, and referral data can\'t be exported or reused.'],
  ['Do I have to say I\'m earning a commission?', 'Yes, every time you recommend GoGrov. We give you wording you can use.'],
  ['What if I\'m declined?', 'Nothing changes about your subscription, and you\'re welcome to apply again in 90 days.'],
]
